// src/components/listing/ReviewsSection.js
// Section avis/étoiles complète - avis publics, notation utilisateurs connectés, suppression admin - NE PAS MODIFIER

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import apiClient from '../../api/client';
import StarRating from './StarRating';

const ReviewsSection = ({ listingId, onStatsChange }) => {
  const { theme } = useTheme();
  const { user, isAuthenticated, isAdminAuthenticated } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [average, setAverage] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.get(`/endpoints/reviews.php?action=list&listingId=${listingId}`);
      setReviews(result.reviews || []);
      setAverage(result.average || 0);
      setCount(result.count || 0);
      if (onStatsChange) onStatsChange({ average: result.average || 0, count: result.count || 0 });

      // Pré-remplit le formulaire si l'utilisateur a déjà laissé un avis
      if (user) {
        const mine = (result.reviews || []).find((r) => r.user_id === user.id);
        if (mine) {
          setMyRating(mine.rating);
          setMyComment(mine.comment || '');
        }
      }
    } catch (err) {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [listingId, user, onStatsChange]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleSubmitReview = async () => {
    if (myRating === 0) {
      alert('Choisissez une note en étoiles');
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post('/endpoints/reviews.php?action=create', {
        listingId,
        rating: myRating,
        comment: myComment,
      });
      setShowForm(false);
      loadReviews();
    } catch (err) {
      alert(err.message || "Erreur lors de l'envoi de l'avis");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      await apiClient.delete(`/endpoints/reviews.php?action=delete&id=${reviewId}`);
      loadReviews();
    } catch (err) {
      alert("Erreur lors de la suppression");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', gap: '16px' },
    headerRow: { display: 'flex', alignItems: 'center', gap: '10px' },
    sectionTitle: { fontSize: '16px', fontWeight: '700', color: theme.textPrimary },
    averageText: { fontSize: '14px', color: theme.textSecondary },
    writeButton: {
      alignSelf: 'flex-start', padding: '10px 18px', backgroundColor: theme.goldSoft,
      border: `1px solid ${theme.goldBorder}`, borderRadius: '10px', color: theme.gold,
      fontSize: '13px', fontWeight: '700', cursor: 'pointer',
    },
    formBox: {
      display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px',
      backgroundColor: theme.bgCard, borderRadius: '12px', border: `1px solid ${theme.border}`,
    },
    formLabel: { fontSize: '13px', fontWeight: '600', color: theme.textSecondary },
    textarea: {
      padding: '12px 14px', backgroundColor: theme.bgInput, border: `1px solid ${theme.border}`,
      borderRadius: '8px', color: theme.textPrimary, fontSize: '14px', outline: 'none',
      minHeight: '80px', resize: 'vertical', fontFamily: 'inherit',
    },
    formActions: { display: 'flex', gap: '10px' },
    cancelBtn: {
      flex: 1, padding: '11px', backgroundColor: 'transparent', border: `1px solid ${theme.border}`,
      borderRadius: '8px', color: theme.textSecondary, fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    },
    submitBtn: {
      flex: 1, padding: '11px', backgroundColor: theme.gold, border: 'none',
      borderRadius: '8px', color: '#FFFFFF', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
    },
    loginHint: { fontSize: '13px', color: theme.textMuted, fontStyle: 'italic' },
    reviewCard: {
      display: 'flex', flexDirection: 'column', gap: '6px', padding: '14px',
      backgroundColor: theme.bgCard, borderRadius: '12px', border: `1px solid ${theme.borderSoft}`,
    },
    reviewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    reviewUserGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
    reviewUserName: { fontSize: '14px', fontWeight: '700', color: theme.textPrimary },
    reviewDate: { fontSize: '11px', color: theme.textMuted },
    deleteReviewBtn: {
      padding: '4px 10px', backgroundColor: 'rgba(245, 101, 101, 0.1)', border: 'none',
      borderRadius: '6px', color: '#F56565', fontSize: '11px', fontWeight: '600', cursor: 'pointer',
    },
    reviewComment: { fontSize: '13px', color: theme.textSecondary, lineHeight: '1.6', marginTop: '4px' },
    emptyText: { fontSize: '13px', color: theme.textMuted, textAlign: 'center', padding: '16px' },
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <div style={styles.sectionTitle}>⭐ Avis</div>
        {count > 0 && (
          <>
            <StarRating rating={Math.round(average)} size={15} />
            <span style={styles.averageText}>{average} · {count} avis</span>
          </>
        )}
      </div>

      {isAuthenticated && !showForm && (
        <button style={styles.writeButton} onClick={() => setShowForm(true)}>
          {myRating > 0 ? 'Modifier mon avis' : 'Laisser un avis'}
        </button>
      )}

      {isAuthenticated && showForm && (
        <div style={styles.formBox}>
          <label style={styles.formLabel}>Votre note</label>
          <StarRating rating={myRating} size={26} interactive onChange={setMyRating} />
          <label style={styles.formLabel}>Votre commentaire (optionnel)</label>
          <textarea
            style={styles.textarea}
            value={myComment}
            onChange={(e) => setMyComment(e.target.value)}
            placeholder="Partagez votre expérience..."
          />
          <div style={styles.formActions}>
            <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Annuler</button>
            <button style={styles.submitBtn} onClick={handleSubmitReview} disabled={submitting}>
              {submitting ? 'Envoi...' : 'Publier'}
            </button>
          </div>
        </div>
      )}

      {!isAuthenticated && (
        <div style={styles.loginHint}>Connectez-vous pour laisser un avis sur cette annonce.</div>
      )}

      {!loading && reviews.length === 0 && (
        <div style={styles.emptyText}>Aucun avis pour le moment. Soyez le premier à en laisser un !</div>
      )}

      {reviews.map((review) => (
        <div key={review.id} style={styles.reviewCard}>
          <div style={styles.reviewHeader}>
            <div style={styles.reviewUserGroup}>
              <span style={styles.reviewUserName}>{review.user_name}</span>
              <StarRating rating={review.rating} size={13} />
              <span style={styles.reviewDate}>{formatDate(review.created_at)}</span>
            </div>
            {isAdminAuthenticated && (
              <button style={styles.deleteReviewBtn} onClick={() => handleDeleteReview(review.id)}>
                Supprimer
              </button>
            )}
          </div>
          {review.comment && <div style={styles.reviewComment}>{review.comment}</div>}
        </div>
      ))}
    </div>
  );
};

export default ReviewsSection;